import { ProjectItem, ProjectSection } from '../../types/project';

const DOCUMENT_SECTION_HEADING_PREFIX = '## ';
const LEADING_HEADING_REGEX = /^(#{1,6})\s+(.+?)\s*$/;
const GENERIC_SECTION_HEADING_REGEX = /^##\s+(.+?)\s*$/gm;
const SECTION_PARSE_ERROR_MESSAGE =
  'Unable to save because the document section headings no longer match. Keep the section headings in place and try again.';

export interface ParsedReviewDocumentSection {
  id: string;
  content: string;
}

const normalizeLineEndings = (value: string): string => value.replace(/\r\n?/g, '\n');

const normalizeHeadingText = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase();

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripDuplicateLeadingHeading = (
  content: string,
  title: string,
): string => {
  const normalizedContent = normalizeLineEndings(content).trim();

  if (!normalizedContent) {
    return '';
  }

  const [firstLine, ...remainingLines] = normalizedContent.split('\n');
  const headingMatch = LEADING_HEADING_REGEX.exec(firstLine.trim());

  if (!headingMatch) {
    return normalizedContent;
  }

  if (
    normalizeHeadingText(headingMatch[2] ?? '') !==
    normalizeHeadingText(title)
  ) {
    return normalizedContent;
  }

  return remainingLines.join('\n').trimStart();
};

const getSectionHeadingMatches = (
  markdown: string,
  documentSections: ProjectSection[],
): Array<{ heading: string; index: number; title: string }> => {
  if (documentSections.length === 0) {
    return [];
  }

  const sectionTitlePattern = documentSections
    .map((section) => escapeRegExp(section.title.trim()))
    .join('|');

  if (!sectionTitlePattern) {
    return [];
  }

  const headingRegex = new RegExp(
    `^##\\s+(${sectionTitlePattern})\\s*$`,
    'gm',
  );

  return Array.from(markdown.matchAll(headingRegex)).map((match) => ({
    heading: match[0],
    index: match.index ?? 0,
    title: match[1] ?? '',
  }));
};

export const buildReviewSectionMarkdown = (section: ProjectSection): string => {
  const trimmedContent = section.content?.trim() ?? '';
  const trimmedTitle = section.title?.trim() ?? '';

  if (!trimmedContent) {
    return '';
  }

  if (!trimmedTitle) {
    return trimmedContent;
  }

  const sectionBody = stripDuplicateLeadingHeading(trimmedContent, trimmedTitle);

  return sectionBody
    ? `${DOCUMENT_SECTION_HEADING_PREFIX}${trimmedTitle}\n\n${sectionBody}`
    : `${DOCUMENT_SECTION_HEADING_PREFIX}${trimmedTitle}`;
};

export const buildCombinedReviewMarkdown = (
  project: ProjectItem,
  documentSections: ProjectSection[],
): string => {
  let combinedMarkdown = `# ${project.title}\n\n`;

  if (project.description) {
    combinedMarkdown += `${project.description}\n\n---\n\n`;
  }

  combinedMarkdown += documentSections
    .map((section) => buildReviewSectionMarkdown(section))
    .filter((sectionMarkdown) => sectionMarkdown.length > 0)
    .join('\n\n');

  return combinedMarkdown;
};

export const parseCombinedReviewMarkdown = (
  markdown: string,
  documentSections: ProjectSection[],
): ParsedReviewDocumentSection[] => {
  const normalizedMarkdown = normalizeLineEndings(markdown);

  if (documentSections.length === 0) {
    return [];
  }

  const exactHeadingMatches = getSectionHeadingMatches(
    normalizedMarkdown,
    documentSections,
  );

  const hasExactBoundaryMatches =
    exactHeadingMatches.length === documentSections.length &&
    exactHeadingMatches.every((match, index) =>
      normalizeHeadingText(match.title) ===
      normalizeHeadingText(documentSections[index].title),
    );

  let sectionBoundaries = exactHeadingMatches;

  if (!hasExactBoundaryMatches) {
    sectionBoundaries = Array.from(
      normalizedMarkdown.matchAll(GENERIC_SECTION_HEADING_REGEX),
    ).map((match) => ({
      heading: match[0],
      index: match.index ?? 0,
      title: match[1] ?? '',
    }));
  }

  if (sectionBoundaries.length !== documentSections.length) {
    throw new Error(SECTION_PARSE_ERROR_MESSAGE);
  }

  return documentSections.map((section, index) => {
    const sectionStart =
      sectionBoundaries[index].index + sectionBoundaries[index].heading.length;
    const sectionEnd =
      index + 1 < sectionBoundaries.length
        ? sectionBoundaries[index + 1].index
        : normalizedMarkdown.length;

    return {
      id: section.id,
      content: normalizedMarkdown.slice(sectionStart, sectionEnd).trim(),
    };
  });
};